import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';

export default function Detail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
          <i className="ri-film-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">没有找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-600">它可能已被移除，或链接有误。</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600"
        >
          返回影视分类
        </Link>
      </div>
    );
  }

  const related = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => ({
      movie: m,
      score:
        m.genres.filter((g) => movie.genres.includes(g)).length * 2 + (m.category === movie.category ? 1 : 0),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
    .map((x) => x.movie);

  const meta = [
    { label: '类型', value: movie.category },
    { label: '导演', value: movie.director },
    { label: '上映时间', value: `${movie.year}年` },
    { label: '地区', value: movie.region },
    { label: '片长', value: movie.duration },
    { label: '题材', value: movie.genres.join(' / ') },
  ];

  return (
    <>
      <Seo
        title={`${movie.title}剧情介绍与演员信息 - 奸情影视大全`}
        description={`《${movie.title}》${movie.category}，${movie.year}年${movie.region}作品，导演${movie.director}，主演${movie.cast.join('、')}。${movie.synopsis}`}
        keywords={`${movie.title},剧情介绍,演员,${movie.genres.join(',')},奸情影视`}
      />

      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0 h-[420px] md:h-[460px]">
          <img
            src={movie.heroes[0]}
            alt={`${movie.title}剧情背景图`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/90 to-background-50"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-4 pb-4 pt-8 md:px-6 md:pt-12">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-500 md:text-sm" aria-label="面包屑导航">
            <Link to="/" className="transition-colors hover:text-foreground-950">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category" className="transition-colors hover:text-foreground-950">影视分类</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category?category=${movie.category}`}
              className="transition-colors hover:text-foreground-950"
            >
              {movie.category}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-950">{movie.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="w-full shrink-0 overflow-hidden rounded-lg border border-background-200 md:w-64" style={{ height: 380 }}>
              <img
                src={movie.cover}
                alt={movie.coverAlt}
                title={`${movie.title}剧情介绍`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="flex flex-1 flex-col">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-foreground-950">
                  {movie.category}
                </span>
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category?genre=${g}`}
                    className="rounded-md bg-secondary-200 px-2.5 py-1 text-xs text-secondary-950 transition-colors hover:bg-secondary-300"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{movie.title}</h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
                <span className="text-accent-400">
                  <i className="ri-star-fill mr-1"></i>
                  {movie.rating.toFixed(1)} 分
                </span>
                <span>{movie.year}年</span>
                <span>{movie.region}</span>
                <span>{movie.duration}</span>
              </div>

              <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {meta.map((item) => (
                  <div key={item.label} className="flex gap-3 border-b border-background-200 pb-2">
                    <dt className="w-16 shrink-0 text-sm text-foreground-500">{item.label}</dt>
                    <dd className="text-sm text-foreground-900">{item.value}</dd>
                  </div>
                ))}
                <div className="flex gap-3 border-b border-background-200 pb-2 sm:col-span-2">
                  <dt className="w-16 shrink-0 text-sm text-foreground-500">主演</dt>
                  <dd className="flex flex-wrap gap-2">
                    {movie.cast.map((name) => (
                      <span key={name} className="text-sm text-foreground-900">{name}</span>
                    ))}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/category?category=${movie.category}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{movie.category}分类
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500"
                >
                  浏览更多作品
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <section>
          <div className="mb-5 flex items-center gap-3">
            <span className="h-7 w-1.5 rounded-full bg-primary-500"></span>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">剧情介绍</h2>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
            <p className="text-sm leading-loose text-foreground-700 md:text-base">{movie.plot}</p>
          </div>
        </section>

        <section className="mt-12">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-7 w-1.5 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">演员信息</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {movie.actors.map((actor) => (
              <div
                key={actor.name}
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-500/15 font-heading text-lg font-bold text-primary-300">
                  {actor.name.slice(0, 1)}
                </span>
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground-950">{actor.name}</p>
                  <p className="truncate text-xs text-foreground-500">{actor.role}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-12">
            <div className="mb-5 flex items-center gap-3">
              <span className="h-7 w-1.5 rounded-full bg-primary-500"></span>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}